package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.product.styles.ChecklistInteractiveStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATIONS: Drop In + Scale Image
@Composable
private fun Modifier.staggeredDropIn(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else (-30).px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
private fun Modifier.scaleInSlow(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.9f).transition(
        Transition.of("opacity", 1.2.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 1.2.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
fun ProductNeuroscienceBenefitsSection(data: ProductNeuroscienceSectionData = defaultProductNeuroscienceData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-neuroscience-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.checklist.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).staggeredDropIn(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionDescription.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px).staggeredDropIn(isVisible, 150)
                                .toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionDescription; onDispose {}
                                    }
                                })
                    }

                    if (isDesktop) {
                        Row(
                            modifier = Modifier.fillMaxWidth().gap(100.px),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            NeuroscienceChecklist(
                                data.checklist, data.checklistIconPath, Modifier.weight(1f), isVisible
                            )
                            if (data.mainImagePath.isNotBlank()) Image(
                                src = data.mainImagePath,
                                modifier = Modifier.weight(1f).fillMaxWidth().height(700.px).borderRadius(12.px)
                                    .objectFit(ObjectFit.Cover).scaleInSlow(isVisible)
                            )
                        }
                    } else {
                        Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                            NeuroscienceChecklist(
                                data.checklist,
                                data.checklistIconPath,
                                Modifier.fillMaxWidth().margin(bottom = 40.px),
                                isVisible
                            )
                            Image(
                                src = data.mainImagePath,
                                modifier = Modifier.unextractable().fillMaxWidth().height(325.px).borderRadius(12.px)
                                    .objectFit(ObjectFit.Cover).scaleInSlow(isVisible)
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NeuroscienceChecklist(
    checklist: List<ChecklistData>, iconPath: String, modifier: Modifier, isVisible: Boolean
) {
    Column(modifier = modifier.gap(4.px)) {
        checklist.forEachIndexed { index, item ->
            key(index) {
                ChecklistItem(
                    title = item.title,
                    desc = item.description,
                    iconPath = iconPath,
                    modifier = Modifier.staggeredDropIn(isVisible, 300 + (index * 150))
                )
            }
        }
    }
}

@Composable
private fun ChecklistItem(title: String, desc: String, iconPath: String, modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()
    Row(
        verticalAlignment = Alignment.Top,
        modifier = ChecklistInteractiveStyle.toModifier().then(modifier).fillMaxWidth()
    ) {
        if (iconPath.isNotBlank()) Image(
            src = iconPath, modifier = Modifier.size(24.px).margin(right = 16.px, top = 2.px)
        )
        Column {
            if (title.isNotBlank()) SpanText(
                text = title, modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold)
            )
            if (desc.isNotBlank()) Div(
                attrs = Modifier.color(palette.custom.darkText).fontSize(16.px).lineHeight(1.5)
                    .toAttrs { ref { htmlElement -> htmlElement.innerHTML = desc; onDispose {} } })
        }
    }
}

// Create the Data Class (This mimics your future CMS payload)
/**
 * CMS IMAGE UPLOAD SPECS:
 * @field mainImagePath:
 * - Aspect Ratio: Flexible (Landscape/Portrait).
 * - Recommended Dimensions: 600x600px.
 * - Max File Size: 300KB.
 * - Behavior: Cropped to 500px height (Desktop) via ObjectFit.Cover.
 * @field checklistIconPath:
 * - Aspect Ratio: 1:1.
 * - Recommended Dimensions: 24x24px.
 * - Format: SVG ONLY.
 */
data class ChecklistData(val title: String, val description: String)
data class ProductNeuroscienceSectionData(
    val sectionId: String? = null,
    val sectionHeader: String,
    val sectionDescription: String,
    val mainImagePath: String,
    val checklistIconPath: String,
    val checklist: List<ChecklistData>
)

val defaultProductNeuroscienceData = ProductNeuroscienceSectionData(
    sectionId = "product-neuroscience-section",
    sectionHeader = "TRIGGERING SELF-INITIATED NEUROPLASTICITY",
    sectionDescription = "The key long-term goal of hand rehabilitation involves retraining the brain using the principles of neuroplasticity rather than just exercising the muscles. \n\nIRegained has developed the following proprietary method to trigger neuroplasticity:",
    mainImagePath = Res.Images.ProductPage.Neuroscience.CLINICIAN_PATIENT,
    checklistIconPath = Res.Images.ProductPage.Neuroscience.ICON_CHECK,
    checklist = listOf(
        ChecklistData(
            "Personalized Rehabilitation",
            "Individual digit isolation & adjustable force and range of motion elements tailored to individual patient capability."
        ),
        ChecklistData(
            "Intrinsic Motivation",
            "Therapy must be self-initiated and self-directed for voluntary muscle control. Active brain engagement is a prerequisite for neuroplastic changes."
        ),
        ChecklistData(
            "Sensory Engagement",
            "Leverages proprioception & indirect real-time visual feedback of finger movements for error-corrective learning."
        ),
        ChecklistData("Active Motor Recovery", "High repetition of functional grasp and pinch movements."),
        ChecklistData(
            "Highly engaging Interface",
            "Easy to use and highly engaging games, make the therapy highly engaging and improve adherence."
        ),
        ChecklistData(
            "Clinical Assessment", "Embedded assessment tools to track progress and adjust the therapy program."
        )
    )
)